import type { NearestStores } from '@flipp/shopper-ad-web-schemas';
import {
  asNumber,
  type CustomPosition,
  type DynamicModuleConfig,
  DynamicModuleType,
  findNearestStore,
  type KevelCustomData,
  Logger,
} from '@flipp/shopper-ad-web-shared';

export type MobileSwipeItemFeedLocalizedFields = {
  pubUrl?: string;
};

export type MobileSwipeItemFeedConfig = MobileSwipeItemFeedLocalizedFields & {
  type: 'item_feed';
  isActive: true;
  pageIndex: number;
  templateUrl?: string;
  merchantName?: string;
  merchantId?: number;
  storeCode?: string;
  hostedUrl?: string;
  localized?: Record<string, MobileSwipeItemFeedLocalizedFields>;
};

export type MobileSwipeBannerLocalizedFields = {
  bannerUrl?: string;
  ttm_url?: string;
};

export type MobileSwipeBannerConfig = MobileSwipeBannerLocalizedFields & {
  type: 'banner';
  isActive: true;
  pageIndex: number;
  merchantName?: string;
  merchantId?: number;
  localized?: Record<string, MobileSwipeBannerLocalizedFields>;
};

export type MobileSwipeDynamicModuleConfig =
  | MobileSwipeItemFeedConfig
  | MobileSwipeBannerConfig;

const BANNER_STORE_ID_PLACEHOLDER = 'storeID';

const substituteStoreId = (
  url: string | undefined,
  storeId: number | string | undefined,
): string | undefined =>
  url && storeId !== undefined && storeId !== null
    ? url.replace(BANNER_STORE_ID_PLACEHOLDER, String(storeId))
    : url;

const SFML_ANCHOR_PATTERN = /sfml-anchor-id\s*=\s*['"]?(\d+)/;

export const resolveMobileSwipeDynamicModulePageIndex = (
  customPosition?: Readonly<Partial<CustomPosition>> | null,
): number => {
  if (!customPosition?.selector) return 0;

  const match = SFML_ANCHOR_PATTERN.exec(customPosition.selector);
  if (!match) {
    Logger.warn(
      `MobileSwipe: unsupported dynamicModule customPosition selector "${customPosition.selector}", using page 0`,
    );
    return 0;
  }

  const anchor = Number(match[1]);
  const isAfter =
    customPosition.method === 'after' || customPosition.method === 'append';

  return isAfter ? anchor + 1 : anchor;
};

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

export type ResolveMobileSwipeDynamicModuleContext = {
  customData?: Readonly<Partial<KevelCustomData>>;
  nearestStores?: Readonly<NearestStores>;
  pageUrl?: string;
};

export const resolveMobileSwipeDynamicModule = ({
  customData,
  nearestStores,
  pageUrl,
}: Readonly<ResolveMobileSwipeDynamicModuleContext>):
  | MobileSwipeDynamicModuleConfig
  | undefined => {
  const dynamicModule = customData?.dynamicModule as
    | Readonly<Partial<DynamicModuleConfig>>
    | undefined;
  if (!dynamicModule || dynamicModule.isActive !== true) return undefined;
  if (
    dynamicModule.type !== DynamicModuleType.ITEM_FEED &&
    dynamicModule.type !== DynamicModuleType.BANNER
  ) {
    return undefined;
  }

  const params = (() => {
    try {
      return new URL(pageUrl ?? window.location.href).searchParams;
    } catch {
      return new URLSearchParams();
    }
  })();

  const testMerchantId = params.get('flipp-test-merchant-id');
  const merchantId =
    (testMerchantId ? asNumber(testMerchantId) : undefined) ||
    asNumber(customData?.merchantId) ||
    asNumber(dynamicModule.merchantId) ||
    undefined;

  const pageIndex = resolveMobileSwipeDynamicModulePageIndex(
    dynamicModule.customPosition,
  );

  if (dynamicModule.type === DynamicModuleType.BANNER) {
    const storeId = findNearestStore(nearestStores)?.id;
    const bannerLocalized = dynamicModule.localized as
      | Record<string, Partial<MobileSwipeBannerLocalizedFields> | undefined>
      | undefined;

    return {
      type: 'banner',
      isActive: true,
      pageIndex,
      bannerUrl: substituteStoreId(
        nonEmptyString(dynamicModule.bannerUrl),
        storeId,
      ),
      ttm_url: nonEmptyString(dynamicModule.ttm_url),
      merchantName: nonEmptyString(dynamicModule.merchantName),
      merchantId,
      localized: bannerLocalized
        ? Object.fromEntries(
            Object.entries(bannerLocalized).map(([locale, fields]) => [
              locale,
              {
                bannerUrl: substituteStoreId(
                  nonEmptyString(fields?.bannerUrl),
                  storeId,
                ),
                ttm_url: nonEmptyString(fields?.ttm_url),
              },
            ]),
          )
        : undefined,
    };
  }

  const localized = dynamicModule.localized as
    | Record<string, Partial<MobileSwipeItemFeedLocalizedFields> | undefined>
    | undefined;

  return {
    type: 'item_feed',
    isActive: true,
    pageIndex,
    pubUrl: nonEmptyString(dynamicModule.pubUrl),
    templateUrl:
      nonEmptyString(params.get('flipp-test-template-url')) ??
      nonEmptyString(dynamicModule.templateUrl),
    merchantName: nonEmptyString(dynamicModule.merchantName),
    merchantId,
    storeCode:
      nonEmptyString(params.get('flipp-test-store-code')) ??
      nonEmptyString(customData?.storeCode),
    hostedUrl: nonEmptyString(customData?.hostedUrl),
    localized: localized
      ? Object.fromEntries(
          Object.entries(localized).map(([locale, fields]) => [
            locale,
            { pubUrl: nonEmptyString(fields?.pubUrl) },
          ]),
        )
      : undefined,
  };
};
